import { useEffect, useMemo, useRef, useState } from "react";
import { chargeSignature, chargeSourceKey, requiresChargeReview, reviewKey, saveChargeReview, subscribeChargeReviews } from "../services/chargeReviewService";

export const useChargeReviews = (records, month, ready) => {
  const [reviews, setReviews] = useState(null);
  const [error, setError] = useState("");
  const pending = useRef(new Set());
  useEffect(() => subscribeChargeReviews(setReviews, () => setError("Charge review history could not be loaded.")), []);
  useEffect(() => {
    if (!ready || !reviews) return;
    records.forEach((row) => {
      const key = reviewKey(row, month);
      const saved = reviews.find((item) => item.reviewId === key);
      let status;
      if (!saved && requiresChargeReview(row)) {
        const exception = reviews.filter((item) => item.sourceKey === chargeSourceKey(row) && item.billingMonth < month)
          .sort((a, b) => b.billingMonth.localeCompare(a.billingMonth))[0];
        status = exception?.actionStatus === "approve-exception" && exception.signature === chargeSignature(row)
          ? "approve-exception" : "take-action";
      } else if (saved && !requiresChargeReview(row) && saved.actionStatus !== "corrected-at-source" && saved.signature !== chargeSignature(row)) {
        status = "corrected-at-source";
      }
      if (!status || pending.current.has(key)) return;
      pending.current.add(key);
      saveChargeReview(row, month, status, { automatic: true, expectedStatus: saved?.actionStatus || null })
        .catch(() => setError("A charge review could not be saved. Please reload to retry."))
        .finally(() => pending.current.delete(key));
    });
  }, [records, month, ready, reviews]);
  const takeAction = async (row, status) => {
    setError("");
    try { await saveChargeReview(row, month, status); }
    catch (failure) { setError(failure.message === "Correct the source charge first. It still requires review." ? failure.message : "Status could not be saved. Please try again."); throw failure; }
  };
  const activeRecords = useMemo(() => records.filter((row) => {
    const saved = reviews?.find((item) => item.reviewId === reviewKey(row, month));
    return !saved || saved.actionStatus === "take-action";
  }), [records, reviews, month]);
  const logs = useMemo(() => (reviews || []).filter((row) => row.billingMonth === month && row.actionStatus !== "take-action"), [reviews, month]);
  return { activeRecords, logs, takeAction, reviewError: error, reviewsLoading: reviews === null && !error };
};
